/**
 * TodoLinkNode - メモに Todo を埋め込む Tiptap カスタムノード。
 *
 * - 属性は todoId と表示名のスナップショット (title) だけ持ち、状態は Todo 側を正とする
 * - todoId が無い間は追加ダイアログを開く欄を表示する (todo-link-node-view.tsx)
 * - ノードを消しても Todo 自体は消さない
 */

import { mergeAttributes, Node } from '@tiptap/core'
import type { NodeViewProps } from '@tiptap/react'
import { ReactNodeViewRenderer } from '@tiptap/react'

import { requestTodoInsertDialog, TodoLinkNodeView } from './todo-link-node-view'

declare module '@tiptap/core' {
  interface Commands<ReturnType> {
    todoLink: {
      /** カーソル位置に Todo を挿入する。todoId を省略すると入力欄を出す */
      insertTodoLink: (attrs?: { todoId: number; title: string }) => ReturnType
    }
  }
}

export const TodoLinkNode = Node.create({
  name: 'todoLink',
  group: 'block',
  atom: true,
  selectable: true,
  draggable: true,

  addAttributes() {
    return {
      todoId: {
        default: null as number | null,
        parseHTML: (el) => {
          const n = Number(el.getAttribute('data-todo-id'))
          return Number.isInteger(n) && n > 0 ? n : null
        },
        renderHTML: (attrs) => (attrs.todoId != null ? { 'data-todo-id': String(attrs.todoId) } : {}),
      },
      title: {
        default: '',
        parseHTML: (el) => el.getAttribute('data-title') ?? '',
        renderHTML: (attrs) => ({ 'data-title': String(attrs.title ?? '') }),
      },
    }
  },

  parseHTML() {
    return [{ tag: 'div[data-type="todo-link"]' }]
  },

  renderHTML({ HTMLAttributes }) {
    return ['div', mergeAttributes({ 'data-type': 'todo-link' }, HTMLAttributes)]
  },

  // タイトルでメモを検索できるよう content_text に載せる
  renderText({ node }) {
    return String(node.attrs.title ?? '')
  },

  addNodeView() {
    return ReactNodeViewRenderer(TodoLinkNodeView as (props: NodeViewProps) => React.JSX.Element)
  },

  addCommands() {
    return {
      insertTodoLink:
        (attrs) =>
        ({ chain }) => {
          if (!attrs) requestTodoInsertDialog()
          return chain().insertContent({ type: this.name, attrs }).run()
        },
    }
  },
})
